import { Action, Selector, State, StateContext } from '@ngxs/store';
import { DataUpdatesStateModel } from './data-updates.model';
import { DATA_UPDATES_STATE } from '..';
import { Injectable } from '@angular/core';
import { DataUpdates } from './data-updates.action';
import { tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { DataUpdateService } from 'src/app/shared/services/data-update.service';

@State<DataUpdatesStateModel>({
  name: DATA_UPDATES_STATE,
  defaults: {
    dataUpdates: [],
    fetching: false,
  },
})
@Injectable()
export class DataUpdatesState {
  constructor(
    private router: Router,
    private dataUpdatesService: DataUpdateService,
    private messageService: MessageService,
  ) {}

  @Selector()
  static dataUpdates(state: DataUpdatesStateModel) {
    return state.dataUpdates;
  }

  @Selector()
  static dataUpdateById(state: DataUpdatesStateModel) {
    return (id: number) => state.dataUpdates.find((dataUpdate) => dataUpdate.id === id);
  }

  @Selector()
  static dataUpdatesByProductId(state: DataUpdatesStateModel) {
    return (productId: number | null) => state.dataUpdates.filter((dataUpdate) => dataUpdate.products.find(product => product.id === productId));
  }

  @Selector()
  static fetching(state: DataUpdatesStateModel) {
    return state.fetching;
  }

  @Action(DataUpdates.GetDataUpdates)
  loadDataUpdates({ patchState }: StateContext<DataUpdatesStateModel>) {
    patchState({ fetching: true });
    return this.dataUpdatesService.getDataUpdates().pipe(
      tap({
        next: (dataUpdates) => {
          patchState({
            fetching: false,
            dataUpdates,
          });
        },
        error: (error) => {
          patchState({
            fetching: false,
          });
        }
      })
    );
  }

  @Action(DataUpdates.BulkReplaceDataUpdates)
  bulkReplaceDataUpdates(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.BulkReplaceDataUpdates) {
    return this.dataUpdatesService.bulkReplaceDataUpdates(action.bulkReplaceDataUpdateInput).pipe(
      tap({
        next: (newDataUpdate) => {
          ctx.patchState({
            dataUpdates: [...newDataUpdate],
          })

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Data Updates uploaded successfully.'
          })
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Data Updates upload failed.'
          })
        }
      })
    )
  }

  @Action(DataUpdates.CreateDataUpdate)
  createDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.CreateDataUpdate) {
    return this.dataUpdatesService.createDataUpdate(action.createDataUpdateInput).pipe(
      tap((newDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: [...state.dataUpdates!, newDataUpdate]
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Data update is created successfully.'
        })
      })
    )
  }

  @Action(DataUpdates.UpdateDataUpdate)
  updateDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.UpdateDataUpdate) {
    return this.dataUpdatesService.updateDataUpdate(action.updateDataUpdateInput).pipe(
      tap((updatedDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: state.dataUpdates!.map((dataUpdates) => dataUpdates.id === updatedDataUpdate.id ? updatedDataUpdate : dataUpdates)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Data update is updated successfully.'
        })
      })
    )
  }

  @Action(DataUpdates.RemoveDataUpdate)
  removeDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.RemoveDataUpdate) {
    return this.dataUpdatesService.removeDataUpdate(action.dataUpdateId).pipe(
      tap({
        next: (removedDataUpdate) => {
          const state = ctx.getState()

          ctx.patchState({
            dataUpdates: state.dataUpdates!.filter((dataUpdates) => dataUpdates.id !== removedDataUpdate.id)
          })

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Data update is deleted successfully.'
          });
        },
        error: (err) => {
          console.error(err)
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Deleting data update failed.'
          })
        }
      })
    )
  }

  @Action(DataUpdates.AddProductToDataUpdate)
  addProductToDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.AddProductToDataUpdate) {
    return this.dataUpdatesService.addProductToDataUpdate(action.dataUpdateId, action.productId).pipe(
      tap((updatedDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: state.dataUpdates!.map((dataUpdates) => dataUpdates.id === updatedDataUpdate.id ? updatedDataUpdate : dataUpdates)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Data-update's products is updated successfully.`
        })
      })
    )
  }

  @Action(DataUpdates.AddProductsToDataUpdate)
  addProductsToDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.AddProductsToDataUpdate) {
    return this.dataUpdatesService.addProductsToDataUpdate(action.dataUpdateId, action.productIds).pipe(
      tap((updatedDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: state.dataUpdates!.map((dataUpdates) => dataUpdates.id === updatedDataUpdate.id ? updatedDataUpdate : dataUpdates)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Data-update's products is updated successfully.`
        })
      })
    )
  }

  @Action(DataUpdates.RemoveProductFromDataUpdate)
  removeProductFromDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.RemoveProductFromDataUpdate) {
    return this.dataUpdatesService.removeProductFromDataUpdate(action.dataUpdateId, action.productId).pipe(
      tap((updatedDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: state.dataUpdates!.map((dataUpdates) => dataUpdates.id === updatedDataUpdate.id ? updatedDataUpdate : dataUpdates)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Data-update's products is updated successfully.`
        })
      })
    )
  }

  @Action(DataUpdates.RemoveProductsFromDataUpdate)
  removeProductsFromDataUpdate(ctx: StateContext<DataUpdatesStateModel>, action: DataUpdates.RemoveProductsFromDataUpdate) {
    return this.dataUpdatesService.removeProductsFromDataUpdate(action.dataUpdateId, action.productIds).pipe(
      tap((updatedDataUpdate) => {
        const state = ctx.getState()

        ctx.patchState({
          dataUpdates: state.dataUpdates!.map((dataUpdates) => dataUpdates.id === updatedDataUpdate.id ? updatedDataUpdate : dataUpdates)
        })

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Data-update's products is updated successfully.`
        })
      })
    )
  }
}
